import { lazy, useEffect } from 'react';
import type { JSX } from 'react';

import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { Capacitor } from '@capacitor/core';

import { AndroidBackController } from './utils/android/backNavigation';
import { setNativePullToRefreshEnabled } from './utils/android/pullToRefresh';

import './App.css';

import PrivateRoute from './pages/users/partials/auth/private_route';

const EmptyLayout = lazy(() => import('./layouts/empty'));
// const NewLayout = lazy(() => import('./layouts/new'));
const NoNavLayout = lazy(() => import('./layouts/noNav'));
const WithHeaderLayout = lazy(() => import('./layouts/withHeader'));
const Layout = lazy(() => import('./layouts/layout'));
const UsersIndex = lazy(() => import('./pages/users'));
const ErrorsIndex = lazy(() => import('./pages/errors'));
const WMSIndex = lazy(() => import('./pages/wms'));
const OutboundIndex = lazy(() => import('./pages/outbound'));
const InboundIndex = lazy(() => import('./pages/inbound'));
const InventoryIndex = lazy(() => import('./pages/inventory'));
const ProfileIndex = lazy(() => import('./pages/profile'));

function App() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let activeScrollContainer: HTMLElement | null = null;

    const isVerticallyScrollable = (element: HTMLElement) => {
      const { overflowY } = window.getComputedStyle(element);
      return ['auto', 'overlay', 'scroll'].includes(overflowY) && element.scrollHeight > element.clientHeight;
    };

    const findTouchedScrollContainer = (target: EventTarget | null) => {
      if (!(target instanceof Element)) {
        return null;
      }

      let current: HTMLElement | null = target instanceof HTMLElement ? target : target.parentElement;
      while (current && current !== document.body) {
        if (isVerticallyScrollable(current)) {
          return current;
        }

        current = current.parentElement;
      }

      return document.scrollingElement instanceof HTMLElement && isVerticallyScrollable(document.scrollingElement)
        ? document.scrollingElement
        : null;
    };

    const syncPullToRefreshState = () => {
      setNativePullToRefreshEnabled(activeScrollContainer ? activeScrollContainer.scrollTop <= 0 : window.scrollY <= 0);
    };

    const removeActiveScrollListener = () => {
      activeScrollContainer?.removeEventListener('scroll', syncPullToRefreshState);
      activeScrollContainer = null;
    };

    const onTouchStart = (event: TouchEvent) => {
      const nextScrollContainer = findTouchedScrollContainer(event.target);
      if (activeScrollContainer !== nextScrollContainer) {
        removeActiveScrollListener();
        activeScrollContainer = nextScrollContainer;
        activeScrollContainer?.addEventListener('scroll', syncPullToRefreshState, { passive: true });
      }

      syncPullToRefreshState();
    };

    syncPullToRefreshState();
    document.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart, true);
      removeActiveScrollListener();
      setNativePullToRefreshEnabled(false);
    };
  }, []);

  const evRoute = (path: string, element: JSX.Element, as?: string, layoutElement?: JSX.Element) => {
    const layout = layoutElement || <Layout />;
    return (
      <Route path={path} element={<PrivateRoute as={as} />}>
        <Route element={layout}>
          <Route index element={element} />
        </Route>
      </Route>
    );
  };

  return (
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <AndroidBackController />
      <Routes>
        <Route path="/users" element={<EmptyLayout />}>
          <Route index element={<UsersIndex />} />
          <Route path="xyz" element={<UsersIndex />} />
          <Route path="forgot" element={<UsersIndex />} />
          <Route path="login" element={<UsersIndex />} />
          <Route path="verify" element={<UsersIndex />} />
        </Route>

        {evRoute('/', <WMSIndex />, undefined, <NoNavLayout />)}

        {evRoute('/outbound', <OutboundIndex />, undefined)}
        {evRoute('/outbound/picklists', <OutboundIndex sections="picklists" />, undefined, <NoNavLayout />)}
        {evRoute('/outbound/picklists/new', <OutboundIndex sections="picklists-new" />, undefined, <NoNavLayout />)}
        {evRoute('/outbound/picklists/:id', <OutboundIndex sections="picklists-detail" />, undefined, <NoNavLayout />)}
        {evRoute(
          '/outbound/picklists/edit/:id',
          <OutboundIndex sections="picklists-update" />,
          undefined,
          <NoNavLayout />,
        )}

        {evRoute('/inbound', <InboundIndex />, undefined)}
        {evRoute('/inbound/manual', <InboundIndex sections="manual" />, undefined, <NoNavLayout />)}
        {evRoute('/inbound/manual/:id', <InboundIndex sections="manual-detail" />, undefined, <NoNavLayout />)}
        {evRoute('/inbound/manual/confirm/:id', <InboundIndex sections="manual-confirm" />, undefined, <NoNavLayout />)}
        {evRoute('/inbound/transfer', <InboundIndex sections="transfer" />, undefined, <NoNavLayout />)}
        {evRoute('/inbound/transfer/:id', <InboundIndex sections="transfer-detail" />, undefined, <NoNavLayout />)}
        {evRoute(
          '/inbound/transfer/confirm/:id',
          <InboundIndex sections="transfer-confirm" />,
          undefined,
          <NoNavLayout />,
        )}
        {evRoute('/inbound/return', <InboundIndex sections="return" />, undefined, <NoNavLayout />)}
        {evRoute('/inbound/return/:id', <InboundIndex sections="return-detail" />, undefined, <NoNavLayout />)}
        {evRoute('/inbound/return/confirm/:id', <InboundIndex sections="return-confirm" />, undefined, <NoNavLayout />)}

        {evRoute('/inventory', <InventoryIndex />, undefined)}

        {evRoute('/profile', <ProfileIndex />, undefined)}
        {evRoute(
          '/profile/warehouses',
          <ProfileIndex sections="warehouses" />,
          undefined,
          <WithHeaderLayout pageTitle="Warehouses" />,
        )}
        {evRoute(
          '/profile/languages',
          <ProfileIndex sections="languages" />,
          undefined,
          <WithHeaderLayout pageTitle="Languages" />,
        )}

        <Route path="/restricted" element={<ErrorsIndex section="restricted" />} />

        <Route path="*" element={<ErrorsIndex section="not-found" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
